/* eslint-disable @typescript-eslint/no-explicit-any */
// Cookie utility to handle authentication and user data storage
// Works on both client and server side

import { config } from "./config";
import { UserData, CookieOptions, Storage } from "@/types/components";
import { SESSION_COOKIE_NAME } from "./auth-v2/constants";

// Client-side cookie utilities (for browser use only)
export const ClientCookie = {
  get(name: string): string | null {
    if (typeof window === "undefined") return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(";").shift() || null;
      return cookieValue;
    }
    return null;
  },

  set(name: string, value: string, options: CookieOptions = {}): void {
    if (typeof window === "undefined") return;

    const opts = { ...config.cookies.options, ...options };

    let cookieString = `${name}=${value}`;

    if (opts.path) cookieString += `; path=${opts.path}`;
    if (opts.domain) cookieString += `; domain=${opts.domain}`;
    if (opts.maxAge) cookieString += `; max-age=${opts.maxAge}`;
    if (opts.expires) cookieString += `; expires=${opts.expires.toUTCString()}`;
    if (opts.secure) cookieString += `; secure`;
    if (opts.sameSite) cookieString += `; samesite=${opts.sameSite}`;

    document.cookie = cookieString;
  },

  remove(name: string, options: CookieOptions = {}): void {
    if (typeof window === "undefined") return;

    this.set(name, "", {
      ...options,
      maxAge: -1,
      expires: new Date(0),
    });
  },

  clear(): void {
    if (typeof window === "undefined") return;

    // Clear all known auth cookies
    const cookiesToClear = [
      config.cookies.authToken,
      config.cookies.user,
      config.cookies.session,
      config.cookies.token,
      config.cookies.adminToken,
      config.cookies.adminUser,
    ];

    cookiesToClear.forEach((cookieName) => {
      if (cookieName) {
        this.remove(cookieName, { path: "/" });
      }
    });
  },
};

// Browser storage implementation using cookies
export const storage: Storage = {
  getItem(key: string): string | null {
    return ClientCookie.get(key);
  },

  setItem(key: string, value: string): void {
    ClientCookie.set(key, value);
  },

  removeItem(key: string): void {
    ClientCookie.remove(key, { path: "/" });
  },

  clear(): void {
    ClientCookie.clear();
  },
};

// Authentication-specific storage helpers
export const AuthStorage = {
  // Get current authentication token
  getToken(): string | null {
    return ClientCookie.get(config.cookies.authToken);
  },

  // Get current user data
  getUser(): UserData | null {
    const userData = ClientCookie.get(config.cookies.user);
    console.log("🔍 AuthStorage.getUser() - Raw cookie value:", userData);
    console.log("🔍 AuthStorage.getUser() - Cookie name:", config.cookies.user);

    if (!userData) {
      console.log("❌ AuthStorage.getUser() - No userData cookie found");
      return null;
    }

    try {
      // Decode URL-encoded cookie value before parsing
      const decodedData = decodeURIComponent(userData);
      console.log("🔍 AuthStorage.getUser() - Decoded data:", decodedData);

      const parsed = JSON.parse(decodedData);
      console.log("✅ AuthStorage.getUser() - Parsed user data:", parsed);
      console.log("✅ AuthStorage.getUser() - User ID:", parsed?.id);
      return parsed;
    } catch (error) {
      console.error("❌ AuthStorage.getUser() - JSON parse error:", error);
      console.error("❌ AuthStorage.getUser() - Failed to parse:", userData);
      return null;
    }
  },

  // Set authentication token (httpOnly)
  setToken(token: string): void {
    ClientCookie.set(
      config.cookies.authToken,
      token,
      config.cookies.authOptions
    );
  },

  // Set user data (client-readable)
  setUser(user: UserData): void {
    ClientCookie.set(
      config.cookies.user,
      JSON.stringify(user),
      config.cookies.userOptions
    );
  },

  // Clear all authentication data
  clearAuth(): void {
    // Clear regular auth cookies
    ClientCookie.remove(config.cookies.authToken, { path: "/" });
    ClientCookie.remove(config.cookies.user, { path: "/" });
    ClientCookie.remove(config.cookies.session, { path: "/" });
    ClientCookie.remove(config.cookies.token, { path: "/" });
    ClientCookie.remove(SESSION_COOKIE_NAME, { path: "/" });

    // Clear admin-specific cookies
    ClientCookie.remove(config.cookies.adminToken, { path: "/" });
    ClientCookie.remove(config.cookies.adminUser, { path: "/" });

    // Also clear localStorage (for legacy support and admin)
    if (typeof window !== "undefined") {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
    }
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.getToken() !== null;
  },

  // Get user role
  getUserRole(): string | null {
    const user = this.getUser();
    return user?.role || null;
  },

  // Admin-specific methods
  getAdminToken(): string | null {
    return (
      ClientCookie.get(config.cookies.adminToken) ||
      (typeof window !== "undefined"
        ? localStorage.getItem("admin_token")
        : null)
    );
  },

  getAdminUser(): any | null {
    const adminData =
      ClientCookie.get(config.cookies.adminUser) ||
      (typeof window !== "undefined"
        ? localStorage.getItem("admin_user")
        : null);
    if (!adminData) return null;

    try {
      // Decode URL-encoded cookie value before parsing
      const decodedData = decodeURIComponent(adminData);
      return JSON.parse(decodedData);
    } catch {
      return null;
    }
  },

  setAdminToken(token: string): void {
    ClientCookie.set(
      config.cookies.adminToken,
      token,
      config.cookies.authOptions
    );
    if (typeof window !== "undefined") {
      localStorage.setItem("admin_token", token);
    }
  },

  setAdminUser(user: any): void {
    const userData = JSON.stringify(user);
    ClientCookie.set(
      config.cookies.adminUser,
      userData,
      config.cookies.userOptions
    );
    if (typeof window !== "undefined") {
      localStorage.setItem("admin_user", userData);
    }
  },

  isAdminAuthenticated(): boolean {
    return this.getAdminToken() !== null;
  },
};
